import type { Href } from "expo-router";
import { router } from "expo-router";
import { useState } from "react";
import { View } from "react-native";

import Button from "$src/core/components/atoms/Button/Button";
import Image from "$src/core/components/atoms/Image/Image";
import { AnimatedScrollView } from "$src/core/components/atoms/ScrollView/ScrollView";
import Text from "$src/core/components/atoms/Text/Text";
import TextInput from "$src/core/components/atoms/TextInput/TextInput";
import { TEST_IDS } from "$src/core/constants/test-ids";
import useAnalytics from "$src/core/hooks/use-analytics";
import useApi from "$src/core/hooks/use-api";
import useAuth from "$src/core/hooks/use-auth";
import useConfig from "$src/core/hooks/use-config";
import useStackHeader from "$src/core/hooks/use-stack-header";
import useTranslation from "$src/core/hooks/use-translation";
import Alerter from "$src/core/services/alerting";

import $ from "./LoginPage.styles";
import type { LoginPageProps } from "./types";

const LoginPage: React.FC<LoginPageProps> = ({ redirect }) => {
  const { t } = useTranslation();
  const { logo } = useConfig();
  const { api } = useApi();
  const { login } = useAuth();
  const { trackEvent } = useAnalytics();
  useStackHeader({
    left: "back",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleLogin = async () => {
    try {
      setIsLoading(true);

      await login(email, password);
      trackEvent("login", { result: "successful" });

      if (redirect) {
        router.replace(redirect as Href);
      } else {
        router.navigate("/profiles");
      }
    } catch {
      trackEvent("login", { result: "error" });
      Alerter.error(t("ERROR/AUTH/INVALID_CREDENTIALS"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatedScrollView
      contentContainerStyle={$.container}
      accessibilityRole="list"
      scrollEventThrottle={16}
    >
      <View style={$.form}>
        <Image
          source={logo}
          style={$.logo}
          contentFit="contain"
          contentPosition="left"
          accessibilityRole="image"
          accessibilityLabel={t("COMMON/LOGO_ACCESSIBILITY_LABEL")}
        />

        <Text preset="heading1" accessibilityRole="text" style={$.title}>
          {t("LOGIN_PAGE/HEADING")}
        </Text>

        <View style={$.field}>
          <Text preset="heading5" accessibilityRole="text">
            {t("AUTH/EMAIL/INPUT/LABEL")}
          </Text>
          <TextInput
            autoCapitalize="none"
            autoComplete="email"
            placeholder={t("AUTH/EMAIL/INPUT/PLACEHOLDER")}
            accessibilityLabel={t("AUTH/EMAIL/INPUT/LABEL")}
            accessibilityRole="text"
            onChangeText={setEmail}
            testID={TEST_IDS.login.emailInput}
          />
        </View>
        <View style={$.field}>
          <Text preset="heading5" accessibilityRole="text">
            {t("AUTH/PASSWORD/INPUT/LABEL")}
          </Text>
          <TextInput
            autoCapitalize="none"
            autoComplete="password"
            placeholder={t("AUTH/PASSWORD/INPUT/PLACEHOLDER")}
            accessibilityLabel={t("AUTH/PASSWORD/INPUT/LABEL")}
            accessibilityRole="text"
            onChangeText={setPassword}
            secureTextEntry
            testID={TEST_IDS.login.passwordInput}
          />
        </View>

        <Button
          name="sign_in"
          preset="primary"
          onPress={handleLogin}
          icon="sign-in"
          label={t("LOGIN_PAGE/CTA/LABEL")}
          isLoading={isLoading}
          isDisabled={!email || !password}
          style={$.button}
          testID={TEST_IDS.login.signInButton}
        />

        {api.register && (
          <Button
            name="register_link"
            preset="link"
            onPress={() =>
              router.replace(
                redirect ? `/register?redirect=${redirect}` : "/register",
              )
            }
            label={t("LOGIN_PAGE/REGISTER_LINK")}
            style={$.link}
            testID={TEST_IDS.login.registerButton}
          />
        )}
      </View>
    </AnimatedScrollView>
  );
};

export default LoginPage;
